<template>
  <div id="app">
    <NavigationBar />
    <main class="main-content">
      <router-view />
    </main>
    <FooterComponent />
  </div>
</template>

<script>
import NavigationBar from "@/components/NavigationBar.vue";
import FooterComponent from "@/components/FooterComponent.vue";

export default {
  name: "App",
  components: {
    NavigationBar,
    FooterComponent,
  },
};
</script>

<style>
#app {
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  background: #f8f9fa;
  min-height: 100vh;
  position: relative;
  width: 100%;
  margin: 0;
  padding: 0;
}

/* Ensure content doesn't get hidden behind fixed navbar */
.main-content {
  position: relative;
  z-index: 1;
  width: 100%;
  margin: 0;
  padding: 0;
}

/* Import theme styles */
@import "@/assets/theme.css";

/* Global styles */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  line-height: 1.6;
  margin: 0;
  padding: 0;
  width: 100%;
  overflow-x: hidden;
}

/* Remove default nav styles */
nav {
  padding: 0;
}

nav a {
  font-weight: inherit;
  color: inherit;
}

nav a.router-link-exact-active {
  color: inherit;
}

/* ============================================================
   Shared Editorial Page Hero — reused by Speakers, Agenda,
   Contact, Partners, Sponsorship, Exhibitor, and Registration
   page heros. Matches the Home About header typography.
   ============================================================ */
.ahrs-page-hero {
  position: relative;
  overflow: hidden;
  background-color: #0a3a1f;
  background-image: linear-gradient(
      180deg,
      rgba(17, 149, 73, 0.55) 0%,
      rgba(17, 149, 73, 0.68) 55%,
      rgba(12, 110, 55, 0.78) 100%
    ),
    radial-gradient(
      120% 95% at 86% 6%,
      rgba(22, 168, 90, 0.28) 0%,
      transparent 46%
    ),
    radial-gradient(
      90% 75% at -5% 105%,
      rgba(12, 110, 55, 0.32) 0%,
      transparent 52%
    ),
    var(--hero-photo, none);
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  padding: clamp(110px, 14vh, 180px) clamp(24px, 5vw, 80px)
    clamp(72px, 9vw, 120px);
  width: 100%;
}
.ahrs-page-hero-wrap {
  position: relative;
  z-index: 1;
  max-width: 1240px;
  margin: 0 auto;
  text-align: center;
}
.ahrs-page-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 8px 18px 8px 14px;
  border: 1px solid rgba(251, 247, 236, 0.3);
  border-radius: 100px;
  background: rgba(255, 255, 255, 0.14);
  font-family: "Inter", "Segoe UI", sans-serif;
  font-size: 12.5px;
  font-weight: 600;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: #fbf7ec;
  margin-bottom: 26px;
}
.ahrs-page-eyebrow .dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #16a85a;
  box-shadow: 0 0 0 4px rgba(22, 168, 90, 0.18);
}
.ahrs-page-title {
  font-family: "Playfair Display", "Times New Roman", serif;
  font-weight: 600;
  font-size: clamp(38px, 5vw, 76px);
  line-height: 1.08;
  letter-spacing: -0.018em;
  color: #fbf7ec;
  margin: 0 auto;
  max-width: 940px;
}
.ahrs-page-italic {
  font-style: italic;
  font-weight: 500;
  color: #b8f0c8;
}
.ahrs-page-lede {
  font-family: "Inter", "Segoe UI", sans-serif;
  font-weight: 400;
  font-size: clamp(16px, 1.35vw, 20px);
  line-height: 1.6;
  color: rgba(251, 247, 236, 0.92);
  margin: 24px auto 0;
  max-width: 680px;
}
.ahrs-page-meta {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 28px;
  margin-top: 36px;
}
.ahrs-page-meta .m {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-family: "Inter", "Segoe UI", sans-serif;
  font-size: 14.5px;
  font-weight: 600;
  color: #fbf7ec;
}
.ahrs-page-meta svg {
  width: 18px;
  height: 18px;
  stroke: rgba(251, 247, 236, 0.9);
  stroke-width: 1.8;
  fill: none;
  flex: none;
}
.ahrs-page-stats {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: clamp(28px, 4vw, 56px);
  margin-top: 44px;
  padding-top: 36px;
  border-top: 1px solid rgba(251, 247, 236, 0.25);
  max-width: 760px;
  margin-left: auto;
  margin-right: auto;
}
.ahrs-page-stat {
  text-align: center;
}
.ahrs-page-stat .n {
  display: block;
  font-family: "Playfair Display", "Times New Roman", serif;
  font-size: clamp(28px, 3vw, 40px);
  font-weight: 600;
  line-height: 1;
  letter-spacing: -0.01em;
  font-variant-numeric: tabular-nums;
  color: #fbf7ec;
  margin-bottom: 6px;
}
.ahrs-page-stat .l {
  display: block;
  font-family: "Inter", "Segoe UI", sans-serif;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgba(251, 247, 236, 0.7);
}
@media (max-width: 700px) {
  .ahrs-page-hero {
    padding: clamp(90px, 12vh, 130px) 22px clamp(48px, 7vw, 72px);
  }
  .ahrs-page-meta {
    gap: 18px;
    margin-top: 28px;
  }
  .ahrs-page-stats {
    gap: 22px;
    padding-top: 26px;
    margin-top: 32px;
  }
}

/* ============================================================
   Shared Home-style page background — extracted from HomeView's
   `.unified-background-container` + animated futuristic layer.
   Use by adding the class `ahrs-home-bg` to a page's root div.
   ============================================================ */
.ahrs-home-bg {
  position: relative;
  background-image: url("~@/assets/bg.png");
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-attachment: fixed;
}
/* Direct children sit above the bg image */
.ahrs-home-bg > * {
  position: relative;
  z-index: 1;
}
</style>
